import { Suspense } from 'react';

import type { LinkProps } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Button, ErrorBoundaryWithRetry } from '@/_components';
import { CardContainer, SectionHeading } from '@/_containers';
import type { SvgIconProps } from '@/types';

export function WidgetLayout({
  title,
  icon,
  children,
  className,
  viewAllLink,
  viewAllText,
  footerLeftSlot,
  footerRightSlot,
  headerRightSlot,
  fallback,
  descriptionClassName,
  bodyClassName,
}: {
  title: string;
  icon: string | SvgIconProps;
  viewAllLink?: LinkProps['to'];
  viewAllText?: string;
  children: React.ReactNode;
  className?: string;
  footerLeftSlot?: React.ReactNode;
  footerRightSlot?: React.ReactNode;
  headerRightSlot?: React.ReactNode;
  fallback?: React.ReactNode;
  descriptionClassName?: string;
  bodyClassName?: string;
}) {
  const getRightSlotComponent = () => {
    if (footerRightSlot) {
      return footerRightSlot;
    }
    if (viewAllLink) {
      return (
        <Link to={viewAllLink} className="ml-auto">
          <Button as="link" size="xs" iconRight={ArrowRight}>
            {viewAllText}
          </Button>
        </Link>
      );
    }
    return null;
  };

  return (
    <CardContainer
      className={twMerge('h-full', className)}
      bodyClassName={twMerge('3xl:px-4 flex flex-1 flex-col px-4 pb-0', bodyClassName)}
    >
      <SectionHeading text={title} icon={icon} rightSlot={headerRightSlot} className="flex-0" />
      <div className={twMerge('mt-6 flex-1', descriptionClassName)}>
        <ErrorBoundaryWithRetry>
          <Suspense fallback={fallback}>{children}</Suspense>
        </ErrorBoundaryWithRetry>
      </div>
      <footer className="flex flex-row justify-between py-2 pr-0">
        {footerLeftSlot}
        {getRightSlotComponent()}
      </footer>
    </CardContainer>
  );
}
